import { BackIcon } from 'assets/icons';
import { FC, useState } from 'react';
import { EstimateStatus, EstimateStatusTitles, EstimatesStatuses } from '../constants';
import { RadioWithLabel } from 'components';
import { getEstimateStatusColor } from '../utils';
import ChevronArrowRightReactIcon from 'assets/react-icons/ChevronArrowRightReactIcon';
import { WebAppColors } from 'constants/colors';
import { NotifierTypes } from 'constants/notifierConstants';
import { getNotifications, showNotifier } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';

interface EditEstimateStatusPanelProps {
  status: EstimateStatus;
  onBackClick: () => void;
  handleStatusChange: (estimateStatus: EstimateStatus) => void;
}

const allowedTransitions: Record<EstimateStatus, EstimateStatus[]> = {
  [EstimateStatus.EXPIRED]: Object.keys(EstimateStatus)
    .map(key => EstimateStatus[key as keyof typeof EstimateStatus])
    .filter(status => status !== EstimateStatus.INVOICED),
  [EstimateStatus.REJECTED]: Object.keys(EstimateStatus)
    .map(key => key !== EstimateStatus.EXPIRED && EstimateStatus[key as keyof typeof EstimateStatus])
    .filter(status => status && status !== EstimateStatus.INVOICED),
  [EstimateStatus.PENDING]: Object.keys(EstimateStatus)
    .map(key => EstimateStatus[key as keyof typeof EstimateStatus])
    .filter(status => status !== EstimateStatus.INVOICED),
  [EstimateStatus.ARCHIVED]: [
    EstimateStatus.PENDING,
    EstimateStatus.ACCEPTED,
    EstimateStatus.REJECTED,
    EstimateStatus.ARCHIVED
  ],
  [EstimateStatus.ACCEPTED]: Object.keys(EstimateStatus)
    .map(key => key !== EstimateStatus.EXPIRED && EstimateStatus[key as keyof typeof EstimateStatus])
    .filter(status => status && status !== EstimateStatus.INVOICED),
  [EstimateStatus.INVOICED]: [EstimateStatus.ARCHIVED, EstimateStatus.INVOICED]
};

const EditEstimateStatusPanel: FC<EditEstimateStatusPanelProps> = ({ status, onBackClick, handleStatusChange }) => {
  const [estimateStatusList] = useState(() => {
    const currentStatus = status.toUpperCase() as EstimateStatus;
    const allowedStatuses = allowedTransitions[currentStatus] || [];

    return EstimatesStatuses.filter(item => allowedStatuses.includes(item.value as EstimateStatus));
  });

  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();

  const handleShowNotifer = (id: string, currentStatus, changedStatus) => {
    const existingNotification = notifications.find(notification => notification.id === id);
    if (existingNotification) return;
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: `Cannot change status from ${
            EstimateStatusTitles[currentStatus as keyof typeof EstimateStatusTitles]
          } to ${EstimateStatusTitles[changedStatus as keyof typeof EstimateStatusTitles]}`,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };
  const onStatusChangeClick = (changedStatusParam: EstimateStatus) => {
    const currentStatus = status.toUpperCase() as EstimateStatus;
    const changedStatus = changedStatusParam.toUpperCase() as EstimateStatus;
    if (currentStatus !== changedStatus) {
      const isStatusChangeAllowed =
        allowedTransitions[currentStatus] && allowedTransitions[currentStatus].includes(changedStatus);
      if (!isStatusChangeAllowed) {
        handleShowNotifer(`id-${currentStatus}`, currentStatus, changedStatus);
        return;
      }
      handleStatusChange(changedStatus);
    }
  };

  return (
    <div className="h-full w-96">
      <div className="mb-1 flex items-center border-b border-secondaryBtn py-4">
        <BackIcon className="mx-4 cursor-pointer" id="estimate-details-panel-back-arrow" onClick={onBackClick} />
        <div className="text-17px font-semibold text-headingGray">Status</div>
      </div>
      <div>
        {estimateStatusList.map((estimateStatus, index) => (
          <div
            key={estimateStatus.value}
            className={`flex items-center justify-between py-5 px-4
            ${index !== estimateStatusList.length - 1 && 'border-b border-secondaryBtn'}`}
            onClick={() => {
              onStatusChangeClick(estimateStatus.value as EstimateStatus);
            }}>
            <RadioWithLabel
              isSelected={estimateStatus.value.toLowerCase() === status.toLowerCase()}
              label={estimateStatus.label}
              onClick={() => null}
              labelStyle={{ color: getEstimateStatusColor(estimateStatus.value as EstimateStatus) }}
            />
            {estimateStatus.value.toLowerCase() !== status.toLowerCase() && (
              <ChevronArrowRightReactIcon
                id={`edit-estimate-status-panel-${estimateStatus.value}`}
                fill={WebAppColors.Gray_1}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditEstimateStatusPanel;
